import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";
import CloudDownloadIcon from "../../../../assets/img/icons/CloudDownloadIcon";
import {
  FieldMappingCiroFieldEnum,
  IImportState,
  ImportStepEnum,
} from "./useImportState";

const MAX_CONTACTS_PER_UPLOAD = 10000;

interface IUploadStepProps {
  importState: IImportState;
  onClose?: () => void;
}

const UploadStep: React.FC<IUploadStepProps> = ({ importState, onClose }) => {
  const {
    setStep,
    setCsvData,
    setCsvHeaders,
    mappings,
    setMappings,
    error,
    setError,
  } = importState;

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setError(null);

      // Handle rejected files first
      if (rejectedFiles.length > 0) {
        const rejectedFile = rejectedFiles[0];
        const fileName = rejectedFile.file.name;
        const fileExtension = fileName.split(".").pop()?.toLowerCase();

        let errorMessage = "Invalid file type. Please upload a CSV file.";

        if (fileExtension === "xlsx" || fileExtension === "xls") {
          errorMessage = `"${fileName}" is an Excel file. Please convert it to CSV format and try again.`;
        } else if (fileExtension === "txt") {
          errorMessage = `"${fileName}" is a text file. Please upload a CSV file instead.`;
        } else if (fileExtension) {
          errorMessage = `"${fileName}" (.${fileExtension}) is not supported. Please upload a CSV file.`;
        }

        setError(errorMessage);
        return;
      }

      if (acceptedFiles.length === 0) {
        return;
      }

      const file = acceptedFiles[0];

      // Additional validation for files that might have passed through
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        let errorMessage = "Please upload a CSV file.";

        if (fileExtension === "xlsx" || fileExtension === "xls") {
          errorMessage = `"${file.name}" is an Excel file. Please convert it to CSV format and try again.`;
        } else if (fileExtension) {
          errorMessage = `"${file.name}" (.${fileExtension}) is not supported. Please upload a CSV file.`;
        }

        setError(errorMessage);
        return;
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors && results.errors.length > 0) {
            setError(`Error parsing CSV: ${results.errors[0].message}`);
            return;
          }

          const headers = results.meta.fields || [];
          if (headers.length === 0) {
            setError("The CSV file has no headers");
            return;
          }

          if (results.data.length > MAX_CONTACTS_PER_UPLOAD) {
            setError(
              `You can upload a maximum of ${MAX_CONTACTS_PER_UPLOAD.toLocaleString()} contacts at a time`,
            );
            return;
          }

          setCsvHeaders(headers);
          setCsvData(results.data);

          const synonyms = {
            [FieldMappingCiroFieldEnum.FIRST_NAME]: ["first"],
            [FieldMappingCiroFieldEnum.LAST_NAME]: ["last"],
            [FieldMappingCiroFieldEnum.JOB_TITLE]: ["title"],
            [FieldMappingCiroFieldEnum.COMPANY_NAME]: ["company"],
            [FieldMappingCiroFieldEnum.COMPANY_WEBSITE_DOMAIN]: ["website"],
            [FieldMappingCiroFieldEnum.COMPANY_LINKEDIN_URL]: [
              "linkedin",
              "company",
            ],
            [FieldMappingCiroFieldEnum.CONTACT_LINKEDIN_URL]: ["linkedin"],
          };

          const normalizedHeaders = headers.map((header) => ({
            original: header,
            normalized: header.toLowerCase().replace(/\s+/g, ""),
          }));

          const findBestMatch = (ciroField: FieldMappingCiroFieldEnum) => {
            switch (ciroField) {
              case FieldMappingCiroFieldEnum.CONTACT_LINKEDIN_URL:
                // Look for variations of "person linkedin url", "contact linkedin", etc.
                return normalizedHeaders.find(
                  ({ normalized }) =>
                    normalized.includes("linkedin") &&
                    (normalized.includes("person") ||
                      normalized.includes("contact") ||
                      normalized.includes("profile") ||
                      (normalized.includes("linkedin") &&
                        !normalized.includes("company") &&
                        normalized.includes("url"))),
                );

              case FieldMappingCiroFieldEnum.COMPANY_WEBSITE_DOMAIN:
                // Look for "website" but prefer single "website" over company-specific terms
                return (
                  normalizedHeaders.find(
                    ({ normalized }) =>
                      normalized.includes("website") &&
                      !normalized.includes("company"),
                  ) ||
                  normalizedHeaders.find(({ normalized }) =>
                    normalized.includes("website"),
                  ) ||
                  normalizedHeaders.find(({ normalized }) =>
                    normalized.includes("domain"),
                  )
                );

              case FieldMappingCiroFieldEnum.COMPANY_LINKEDIN_URL:
                // Look for company linkedin specifically
                return normalizedHeaders.find(
                  ({ normalized }) =>
                    normalized.includes("linkedin") &&
                    normalized.includes("company"),
                );

              default:
                // Use original logic for other fields
                const terms = synonyms[ciroField];
                if (!terms) return null;
                return normalizedHeaders.find(({ normalized }) =>
                  terms.every((term) => normalized.includes(term)),
                );
            }
          };

          const newMappings = mappings.map((mapping) => {
            const match = findBestMatch(mapping.ciroField);
            return {
              ...mapping,
              csvField: match?.original || mapping.csvField,
            };
          });

          setMappings(newMappings);
          setStep(ImportStepEnum.MAPPING);
        },
      });
    },
    [mappings, setCsvData, setCsvHeaders, setError, setMappings, setStep],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
    onDropRejected: (rejectedFiles) => {
      // This is handled in the onDrop callback above for better UX
    },
  });

  return (
    <div className={classNames("p-4")}>
      <h2 className={classNames("text-lg", "font-medium", "mb-4")}>
        Import contacts from CSV
      </h2>

      <div
        {...getRootProps()}
        className={classNames(
          "border-2",
          "border-dashed",
          "rounded-md",
          "p-8",
          "text-center",
          "cursor-pointer",
          "hover:bg-gray-50",
          "transition-colors",
          isDragActive ? "border-blue-500" : "border-gray-300",
        )}
      >
        <input {...getInputProps()} />
        <div className={classNames("mb-4", "flex", "justify-center")}>
          <CloudDownloadIcon />
        </div>
        {isDragActive ? (
          <p className={classNames("text-blue-500")}>
            Drop your CSV file here...
          </p>
        ) : (
          <>
            <p className={classNames("mb-1")}>
              Drag and drop a CSV file here, or click to select
            </p>
            <p className={classNames("text-sm", "text-gray-500")}>
              Your CSV should have columns for name, title, company, etc.
            </p>
            <p className={classNames("text-xs", "text-gray-400", "mt-2")}>
              Only CSV files are supported. If you have an Excel file (.xlsx),
              please save it as CSV first.
            </p>
          </>
        )}
      </div>

      {error && (
        <div className={classNames("mt-4", "text-red-500", "text-sm")}>
          {error}
        </div>
      )}

      <div className={classNames("mt-6", "flex", "justify-end", "gap-2")}>
        <CiroButton
          analyticsField={"Cancel import contacts"}
          onClick={onClose}
          style={CiroButtonStyleEnum.INVERTED_LOUD}
        >
          Cancel
        </CiroButton>
      </div>
    </div>
  );
};

export default UploadStep;
